import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SignIn, SignUp } from '@clerk/clerk-react';
import { Button } from '@/shared/ui/button';

type AuthMode = 'signIn' | 'signUp';

type ClerkAppearance = React.ComponentProps<typeof SignIn>['appearance'];

interface ClerkAuthViewProps {
  initialMode?: AuthMode;
}

const appearance: ClerkAppearance = {
  layout: {
    logoImageUrl: '/images/logo-no-background_600_158.png',
    logoPlacement: 'outside',
  },
  variables: {
    colorPrimary: 'hsl(var(--primary))',
    colorPrimaryForeground: 'hsl(var(--primary-foreground))',
    colorBackground: 'hsl(var(--card))',
    colorForeground: 'hsl(var(--foreground))',
    colorMutedForeground: 'hsl(var(--muted-foreground))',
    colorInput: 'hsl(var(--background))',
    colorInputForeground: 'hsl(var(--foreground))',
    colorBorder: 'hsl(var(--border))',
    colorRing: 'hsl(var(--ring))',
    colorDanger: 'hsl(var(--destructive))',
    borderRadius: 'var(--radius)',
  },
  elements: {
    footerAction: { display: 'none' },
  },
};

export function ClerkAuthView({ initialMode = 'signIn' }: ClerkAuthViewProps): React.JSX.Element {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AuthMode>(initialMode);

  const toggleMode = (): void => {
    setMode((currentMode) => (currentMode === 'signIn' ? 'signUp' : 'signIn'));
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-background px-4 py-12 overflow-y-auto">
      {mode === 'signIn' ? (
        <SignIn routing="virtual" forceRedirectUrl="/" appearance={appearance} />
      ) : (
        <SignUp routing="virtual" forceRedirectUrl="/" appearance={appearance} />
      )}

      <Button variant="ghost" size="sm" onClick={toggleMode}>
        {mode === 'signIn' ? t('auth.noAccount') : t('auth.hasAccount')}
      </Button>
    </div>
  );
}
